import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ResultList } from "@/components/dictionary/ResultList";
import { dictionaryAction } from "@/components/dictionary/dictionaryKeys";
import { countsLabel, rowTerm } from "@/components/dictionary/resultKind";
import { useLookup } from "@/components/dictionary/useLookup";
import { TopBar, TopBarDivider } from "@/components/shell/TopBar";
import { isTextEntry } from "@/lib/dom";
import type { OverlayApi } from "@/lib/navBridge";
import type { DeckCounts } from "@/lib/note";

import { isOnCard } from "@/surfaces/read/onCard";
import { RadicalPicker } from "@/surfaces/read/RadicalPicker";
import { insertAtCaret, rememberRadical } from "@/surfaces/read/radicalPalette";

export interface SearchProps {
  /** The query, in the URL — see `navUrl.ts`. Typing replaces rather than pushes. */
  query: string;
  onQueryChange: (query: string) => void;
  isPhone?: boolean;
  /** The tab bar, rendered inside this column — never beside it. See the note in `App.tsx`. */
  footer?: React.ReactNode;
  counts: DeckCounts | null;
  onAdjustCounts: (delta: number) => void;
  /** Layers Back should close — used for the radical picker, as in Read. */
  overlay: OverlayApi;
}

/**
 * 検索 — the dictionary, on its own (日本-497).
 *
 * The results are Read's dock exactly (`components/dictionary/`), so a word looked up here draws the
 * same rows, the same three collection states and the same expand-in-place card. What differs is
 * only what drives it: a text field rather than a hovered token, and therefore no sentence, no
 * images to inherit and no Ask.
 *
 * **The search bar is its own row under the 50px top bar, not inside it.** The top bar keeps the
 * destination word and the two counters, as on every other surface — a field in there would be the
 * only top bar in the app whose shape depends on which screen you are on.
 *
 * **It answers about VOCABULARY and nothing else** (日本-519). 日本-498 put the grammar dictionary
 * in this pool as a second kind and it was the wrong shape: a grammar point is reference material,
 * not a card, so it has its own destination and the two are told apart by where you are rather than
 * by a gutter down every row. Measured before the split — pivot しまう in Read and the DoJG row was
 * #6 of 70 at y=1494px, below the fold on the exact construction the feature was built for.
 *
 * **The request is still `/api/search/all` and the narrowing is `useLookup`'s `kind` option**,
 * which is a deliberate choice rather than a leftover. The route was measured under 2ms with both
 * halves, so asking `/lookup` separately would buy nothing and would put the client-side merge back
 * — the very thing 日本-513 moved to the server so the MCP's `search` tool and this screen could not
 * disagree about an order. What matters is that the narrowing happens where the indices are
 * computed: `focusedResult`, `busyRow` and `expanded.row` all name a position on SCREEN.
 */
export function Search({
  query,
  onQueryChange,
  isPhone = false,
  footer,
  counts,
  onAdjustCounts,
  overlay,
}: SearchProps) {
  const fieldRef = useRef<HTMLInputElement>(null);
  /**
   * Whether an IME conversion is in flight.
   *
   * Two things hang off it and both are load-bearing on a Japanese search box. The query is not
   * sent while composing — the intermediate romaji of 見学 are four lookups of nothing — and Enter
   * does not add, because that keystroke is how the IME commits the conversion. Same guard Ask's
   * composer applies, for the same reason.
   */
  const [composing, setComposing] = useState(false);
  /** What was actually typed, as opposed to what is mid-conversion. */
  const [committed, setCommitted] = useState(query);
  const [pickerOpen, setPickerOpenState] = useState(false);
  const [radicals, setRadicals] = useState<string[]>([]);
  const [recentRadicals, setRecentRadicals] = useState<string[]>([]);
  /** Where the caret was when the picker took focus away — the whole point of inserting *there*. */
  const [caret, setCaret] = useState(0);

  useEffect(() => {
    if (!composing) setCommitted(query);
  }, [query, composing]);

  /**
   * The query, as the dock sees it: **index 0**.
   *
   * `POST /api/search/lookup` pivots on a character position, and asking it about position 0 of the
   * typed text is exactly a dictionary search — it deinflects (食べた answers 食べる) and then offers
   * progressively shorter sub-words below, longest match first. The engine's `lookup_exact` looks
   * like the closer fit and is not: it matches the term verbatim, so a conjugated form answers
   * nothing at all.
   */
  const lookupQuery = useMemo(
    () => (committed.trim() ? { text: committed.trim(), index: 0 } : null),
    [committed],
  );

  const lookup = useLookup({
    query: lookupQuery,
    onAdjustCounts,
    // Longer than Read's 80ms: that one is paced by a pointer sweeping a sentence, this one by
    // typing, and a request per keystroke is a request per keystroke.
    debounceMs: 150,
    // 日本-519 — see the note at the top of this file. Read passes `all` and offers a filter; this
    // screen has one kind and no filter, so `countsLabel(rows)` below draws 語彙 alone.
    kind: "vocab",
  });
  const { rows, focusedResult } = lookup;
  const kinds = useMemo(() => countsLabel(rows), [rows]);

  const setPickerOpen = useCallback(
    (open: boolean) => {
      setPickerOpenState(open);
      if (open) overlay.openOverlay("picker");
      else overlay.closeOverlay("picker");
    },
    [overlay],
  );
  // Closing it *because* of a Back: the entry is already gone, so this must not consume another.
  useEffect(
    () => overlay.registerOverlay("picker", () => setPickerOpenState(false)),
    [overlay],
  );

  const openPicker = useCallback(() => {
    setCaret(fieldRef.current?.selectionStart ?? query.length);
    setPickerOpen(true);
  }, [query.length, setPickerOpen]);

  /**
   * Insert the chosen character at the caret and hand the pane back to results.
   *
   * The picker's contract is unchanged from Read's — it is *"an input method reached from a caret"*,
   * and the search field is a caret. `insertAtCaret` is the same pure helper.
   */
  const insertKanji = useCallback(
    (kanji: string) => {
      const next = insertAtCaret(query, caret, kanji);
      onQueryChange(next.text);
      setCaret(next.caret);
      setPickerOpen(false);
      // Put the caret back where the insertion left it, after the field has the new value.
      window.setTimeout(() => {
        fieldRef.current?.focus();
        fieldRef.current?.setSelectionRange(next.caret, next.caret);
      }, 0);
    },
    [query, caret, onQueryChange, setPickerOpen],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Alt+R is the one shortcut that works *while* typing — it is reached from a caret.
      if (event.altKey && (event.key === "r" || event.key === "R")) {
        event.preventDefault();
        if (pickerOpen) setPickerOpen(false);
        else openPicker();
        return;
      }
      if (pickerOpen) return;

      const inField = event.target === fieldRef.current;
      // A text field that is NOT the search box — the expanded card's own fields — keeps every key.
      // The search box itself does NOT return here: `a`, `e`, `j` and `k` are live inside it, which
      // an IME keystroke can never be mistaken for. See `dictionaryKeys.ts`.
      if (!inField && isTextEntry(event.target)) return;

      if (event.key === "Escape" && inField && query) {
        event.preventDefault();
        onQueryChange("");
        return;
      }

      // **Ctrl+Z takes back the add you just made**, and it is here because `AddedPanel` draws
      // `追加を取り消し Ctrl+Z` on its own — a panel naming a key the surface does not handle is the
      // same lie the hint row and the row's add glyph were both corrected for. It reaches exactly
      // one action; `justAdded` is what keeps it off a card opened with `E`, which has no add to
      // undo and whose note may be years old.
      //
      // Ahead of `dictionaryAction`, which refuses every modified key.
      if ((event.ctrlKey || event.metaKey) && (event.key === "z" || event.key === "Z")) {
        if (lookup.expanded?.justAdded) {
          event.preventDefault();
          void lookup.undo();
        }
        return;
      }

      // **`rows`, not `results`.** 検索 answers about vocabulary alone since 日本-519, so the two
      // lists now hold the same thing — but `focusedResult` names a position on SCREEN and this is
      // where that is decided, so indexing the screen list is what keeps it true if a kind is ever
      // added back. (日本-498 is what made them differ: `A` landed on whichever entry happened to
      // sit at that index in the shorter list, which is a different word.)
      const row = rows[focusedResult];
      const term = row ? (rowTerm(row) ?? "") : "";
      const action = dictionaryAction(event, {
        hasFocus: row !== undefined,
        signal: lookup.carded.get(term) ?? { state: "unknown" },
        onCard: isOnCard(
          lookup.onCardEntries,
          term || null,
          row?.kind === "vocab" ? row.entry.seq : 0,
        ),
        composing,
      });
      if (!action || !row) return;
      event.preventDefault();
      if (action === "next" || action === "prev") {
        lookup.setFocusedResult((i) =>
          action === "next" ? Math.min(i + 1, Math.max(rows.length - 1, 0)) : Math.max(i - 1, 0),
        );
        return;
      }
      if (action === "edit") {
        // Cleared here rather than inside the hook's `edit`, which `add` also calls right after
        // setting the "already in your deck" message — clearing in there would erase it.
        lookup.setError(null);
        void lookup.edit(term, focusedResult);
        return;
      }
      // Vocabulary only since 日本-519 — a grammar row cannot appear here to be acted on.
      if (action === "add" && row.kind === "vocab") {
        void lookup.add(row.entry, focusedResult, null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    rows, focusedResult, lookup, composing, pickerOpen, openPicker, setPickerOpen, query,
    onQueryChange,
  ]);

  const topBar = (
    <TopBar title="検索" subtitle="辞書" destinationId="search">
      {/* Absent rather than zero while the hub is unreachable — a confident `0 追加済` after a
          morning's mining is a worse answer than no answer. Same payload the rail badge reads. */}
      {counts !== null && (
        <>
          <span className="font-mono text-secondary font-semibold text-ink">
            {counts.addedToday}
          </span>
          <span className="text-meta text-ink-3">追加済</span>
          <TopBarDivider />
          <span className="font-mono text-secondary font-semibold text-[color:var(--accent-text)]">
            {counts.new}
          </span>
          <span className="text-meta text-ink-3">未知</span>
        </>
      )}
    </TopBar>
  );

  const searchBar = (
    <div
      className={[
        "flex shrink-0 flex-col gap-[11px] border-b border-line bg-sunken box-border",
        isPhone ? "px-4 pb-[11px] pt-3" : "px-[26px] pb-[14px] pt-4",
      ].join(" ")}
    >
      <div className="flex items-stretch gap-[10px]">
        <div
          className={[
            "flex min-w-0 flex-1 items-center gap-[10px] rounded-[var(--r-3)] border-[1.5px]",
            "border-accent-line bg-panel box-border shadow-[var(--sh-frame)]",
            isPhone ? "h-11 px-3" : "h-12 px-[14px]",
          ].join(" ")}
        >
          <svg
            aria-hidden
            width={isPhone ? 17 : 18}
            height={isPhone ? 17 : 18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            className="shrink-0 text-ink-4"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M16.5 16.5 L21 21" />
          </svg>
          <input
            ref={fieldRef}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onCompositionStart={() => setComposing(true)}
            onCompositionEnd={(event) => {
              setComposing(false);
              // The value at commit, not what `onChange` last saw — the two differ on the keystroke
              // that accepts a conversion.
              setCommitted(event.currentTarget.value);
            }}
            // **A composition left open freezes the search silently**, because `committed` stops
            // following `query` — the field keeps taking input and the results never move, with
            // nothing on screen saying why. `compositionend` is not guaranteed: a field blurred
            // mid-conversion does not always fire one. So blur ends any composition it finds, which
            // costs nothing when there is none.
            onBlur={(event) => {
              setComposing(false);
              setCommitted(event.currentTarget.value);
            }}
            // The caret is here on arrival: this screen exists to be typed into.
            autoFocus
            data-check="search.field"
            aria-label="辞書を検索"
            placeholder="語を入力"
            className={[
              "min-w-0 flex-1 bg-transparent font-jp text-ink outline-none",
              "placeholder:text-ink-off",
              isPhone ? "text-[21px]/[1]" : "text-[24px]/[1]",
            ].join(" ")}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                onQueryChange("");
                fieldRef.current?.focus();
              }}
              aria-label="消去"
              data-check="search.clear"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--r-2)] bg-line-soft text-ink-3"
            >
              <svg
                aria-hidden
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              >
                <path d="M5 5 L19 19 M19 5 L5 19" />
              </svg>
            </button>
          )}
        </div>

        {/* Beside the field rather than in a toolbar: it belongs to this caret. 44px on a phone,
            where it is a real thumb target. */}
        <button
          type="button"
          onMouseDown={(event) => {
            // mousedown, not click — click lands after blur, which would lose the caret.
            event.preventDefault();
            if (pickerOpen) setPickerOpen(false);
            else openPicker();
          }}
          aria-pressed={pickerOpen}
          aria-label="部品検索"
          title="部品検索"
          data-check="search.radicals"
          className={[
            "flex shrink-0 items-center justify-center rounded-[var(--r-3)] border-[1.5px]",
            "box-border shadow-[var(--sh-frame)]",
            isPhone ? "h-11 w-11" : "h-12 w-12",
            pickerOpen
              ? "border-accent bg-accent text-white"
              : "border-accent-line bg-accent-tint text-[color:var(--accent-text)]",
          ].join(" ")}
        >
          <span className={`font-jp leading-none ${isPhone ? "text-[20px]" : "text-[21px]"}`}>
            部
          </span>
        </button>
      </div>

      <div className="flex items-baseline gap-[10px]">
        {pickerOpen ? (
          <>
            <span className="font-jp text-eyebrow font-semibold tracking-[.1em] text-ink-3">
              部品検索
            </span>
            <span className="font-jp text-meta text-ink-4">検索欄のカーソル位置に挿入</span>
          </>
        ) : (
          committed.trim() && (
            <>
              <span className="font-jp text-eyebrow font-semibold tracking-[.1em] text-ink-3">
                {committed.trim()}
              </span>
              {/* **Each kind counted separately** (日本-498). One total cannot say whether the
                  grammar dictionary answered at all, which is the thing this feature exists to
                  make visible — and 文法0件 beside 語彙13件 is the honest answer for a word that
                  is not a construction. The row is omitted rather than drawn as a zero when the
                  kind produced nothing, so an ordinary vocabulary search reads exactly as it did
                  before. */}
              <span data-check="search.counts" className="text-meta text-ink-4">
                {kinds}
              </span>
            </>
          )
        )}
        <span className="h-px flex-1 bg-line" />
        {/* Keyboard hints belong beside the control they trigger — and there is no keyboard here.
            `↑↓` and `↵` rather than `jk` and `A`: the caret is in the field on this screen, so the
            bare letters are withheld (see `dictionaryKeys.ts`) and promising them would be a lie. */}
        {!isPhone && (
          <span className="font-mono text-meta text-ink-4">
            {pickerOpen ? "↵で挿入 · Escで結果一覧へ" : "jkで移動 · Aで追加 · Eで編集 · Alt+Rで部品"}
          </span>
        )}
      </div>
    </div>
  );

  const body = pickerOpen ? (
    // The picker takes the results region and leaves the query on screen above it — the same
    // relationship Read has with the sentence being edited.
    <RadicalPicker
      caretLabel="検索欄"
      selected={radicals}
      onSelectedChange={setRadicals}
      recents={recentRadicals}
      onRemember={(radical) => setRecentRadicals((current) => rememberRadical(current, radical))}
      onInsert={insertKanji}
      onClose={() => setPickerOpen(false)}
      compact={isPhone}
    />
  ) : (
    <div
      className={[
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto box-border",
        isPhone ? "px-4 py-[14px]" : "px-[26px] pb-5 pt-[18px]",
      ].join(" ")}
    >
      {lookup.error && (
        <p
          data-check="search.error"
          className="flex items-center gap-[9px] self-center rounded-[var(--r-3)] border border-accent-border bg-[color:var(--accent-ground)] px-[13px] py-[9px] text-meta text-ink-3"
        >
          {lookup.error}
        </p>
      )}
      <ResultList
        lookup={lookup}
        isPhone={isPhone}
        empty={
          <div
            data-check="search.empty"
            className="flex flex-1 flex-col items-center justify-center gap-[10px] px-4 text-center"
          >
            {committed.trim() ? (
              <>
                <p className="font-jp text-[15px]/[1.6] text-ink-3">
                  辞書に該当する項目がありません。
                </p>
                {/* The English case, and the one that needs saying: the dictionary is
                    Japanese-only, so `cactus` returning nothing looks like the screen is broken
                    rather than like an answer. */}
                <p className="font-jp text-meta text-ink-4">
                  この辞書は日本語だけを引きます。英語やローマ字では引けません。
                </p>
              </>
            ) : (
              <>
                <p className="font-jp text-[15px]/[1.6] text-ink-3">
                  辞書を引く語を入力してください。
                </p>
                {/* 「」 rather than spaces around the examples. Japanese does not space its
                    clauses, and the extractor splits element text on spaces — so the spaced version
                    reached `docs/CHROME.md` as six fragments (`食べた`, `は`, `を引きます。`…), each
                    demanding a gloss of its own and none of them a sentence anybody wrote. */}
                <p className="font-jp text-meta text-ink-4">
                  活用形のままで構いません。「食べた」で「食べる」を引きます。
                </p>
                <p className="font-jp text-meta text-ink-4">読めない字は「部」から。</p>
              </>
            )}
          </div>
        }
        className="flex min-h-0 flex-1 flex-col gap-2"
      />
    </div>
  );

  return (
    <>
      {topBar}
      {searchBar}
      {body}
      {footer}
    </>
  );
}
